/**
 * Home: one page for everyone, sectioned by job (Buying, Receiving, Planning).
 * Each card is one number and one short line; the lists live on To order and Arriving.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { TieOutResponse } from "@march/shared";
import { apiGet } from "../lib/api.ts";
import { CATEGORY_LABEL } from "../lib/categories.ts";
import { shortDate, todayIso, useSupply, weekStart, addDays, refLabel } from "../lib/supply.ts";
import { locale, t } from "../lib/i18n.tsx";
import { useMe } from "../hooks/useMe.tsx";
import type { ReactNode } from "react";

/**
 * A Home card. Factory logins only have Forecast: buying cards open it filtered, receiving cards are
 * plain (no Arriving page for them).
 */
function Card({ to, className, factory, children }: { to: string; className: string; factory: boolean; children: ReactNode }) {
  if (!factory) return <Link className={className} to={to}>{children}</Link>;
  if (to.startsWith("/order")) return <Link className={className} to={to === "/order" ? "/forecast?show=need" : "/forecast"}>{children}</Link>;
  return <div className={className}>{children}</div>;
}

const WELCOME_KEY = "mi.welcome.dismissed";

const when = (ms?: number | string | null) =>
  ms ? new Date(ms).toLocaleString(locale(), { weekday: "short", hour: "numeric", minute: "2-digit" }) : t("not yet");

/** Can I trust today's numbers? Stock sync, last forecast run and last night's NetSuite tie-out, in one line. */
function TrustLine({ factory }: { factory: boolean }) {
  const ingestion = useQuery<{ runs: Array<{ job_name: string; last_started: number }> }>({
    queryKey: ["ingestion-health"],
    queryFn: () => apiGet("/api/health/ingestion"),
  });
  const tieout = useQuery<TieOutResponse>({ queryKey: ["tieout"], queryFn: () => apiGet<TieOutResponse>("/api/tieout/latest") });
  const run = (job: string) => ingestion.data?.runs?.find((r) => r.job_name === job)?.last_started;
  const tie = tieout.data?.latest;
  const tieText = !tie ? t("not run yet") : tie.status === "ok" ? t("matches ({n} parts checked)", { n: tie.skus_checked }) : tie.status === "mismatch" ? t("{n} parts differ", { n: tie.mismatched }) : t("failed");
  const bad = !!tie && tie.status !== "ok";
  return (
    <p className={`trust${bad ? " bad" : ""}`}>
      <span>{t("Stock from NetSuite {when}", { when: when(run("inventory_snapshot")) })}</span>
      <span>{t("Forecast updated {when}", { when: when(run("compute_forecast")) })}</span>
      <span>{t("NetSuite check: {result}", { result: tieText })}</span>
      {!factory && <Link to="/overview">{t("Data status")}</Link>}
    </p>
  );
}

export function HomePage() {
  const s = useSupply();
  const factory = useMe().data?.role === "factory";
  const [welcome, setWelcome] = useState(() => {
    try {
      return localStorage.getItem(WELCOME_KEY) !== "1";
    } catch {
      return true;
    }
  });
  const hideWelcome = () => {
    setWelcome(false);
    try {
      localStorage.setItem(WELCOME_KEY, "1");
    } catch {
      /* private mode: just hide it for now */
    }
  };

  const today = todayIso();
  const weekEnd = addDays(weekStart(today), 6);
  const thisWeek = [...s.deliveries.values()]
    .filter((d) => d.later.some((l) => l.date && l.date >= today && l.date <= weekEnd))
    .sort((a, b) => (a.next ?? "").localeCompare(b.next ?? ""));
  const n = (v: number) => (s.loading ? "…" : v);

  if (s.error) return <div className="u-error">{t("Couldn't load stock and orders:")} {s.error.message}</div>;

  return (
    <div className="u-page">
      {welcome && (
        <div className="welcome">
          <div>
            <b>{t("Parts only.")}</b> {t("Finished umbrellas are in NetSuite. Nothing you do here changes NetSuite.")}
          </div>
          <button type="button" onClick={hideWelcome} aria-label={t("Dismiss")}>✕</button>
        </div>
      )}

      <TrustLine factory={factory} />

      <div className="section first">
        <div className="section-head">
          <h3>{t("Buying")}</h3>
          {!factory && <Link className="go" to="/order">{t("Open To order →")}</Link>}
        </div>
        <div className="cards lead">
          <Card factory={factory} className="card lead-card" to="/order">
            <div className="k">{t("Order now")}</div>
            <div className="v">{n(s.orderNow.length)} <small>{t("parts")}</small></div>
            <div className="line">{t("They'll run out before a new order could arrive")}</div>
            {!s.loading && s.orderNow.length > 0 && (
              <div className="line muted">
                {Object.entries(s.orderNow.reduce<Record<string, number>>((m, r) => { const k = t(CATEGORY_LABEL[r.item_type] ?? r.item_type); m[k] = (m[k] ?? 0) + 1; return m; }, {}))
                  .sort((a, b) => b[1] - a[1]).map(([k, v]) => `${k} ${v}`).join(" · ")}
              </div>
            )}
          </Card>
          <Card factory={factory} className="card warn" to="/order?tab=sooner">
            <div className="k">{t("Ship sooner")}</div>
            <div className="v">{n(s.speedUps.length)} <small>{t("deliveries")}</small></div>
            <div className="line">{t("On order, but arriving after the shelf runs empty")}</div>
          </Card>
          <Card factory={factory} className="card" to="/order?tab=topups">
            <div className="k">{t("Top-ups")}</div>
            <div className="v">{n(s.topUps.length)} <small>{t("parts")}</small></div>
            <div className="line">{t("Can wait")}</div>
          </Card>
        </div>
        {!s.loading && s.orderNowAll.length + s.topUpsAll.length > s.orderNow.length + s.topUps.length && (
          <p className="fine" style={{ marginTop: 6 }}>
            {t("Not counted: {n} parts Operations doesn't keep in stock ({a} would be in Order now, {b} in Top-ups). Nobody needs to buy them here.", {
              n: s.orderNowAll.length + s.topUpsAll.length - s.orderNow.length - s.topUps.length,
              a: s.orderNowAll.length - s.orderNow.length,
              b: s.topUpsAll.length - s.topUps.length,
            })}
          </p>
        )}
      </div>

      <div className="section">
        <div className="section-head">
          <h3>{t("Receiving at Jurupa")}</h3>
          {!factory && <Link className="go" to="/arriving">{t("Open Arriving →")}</Link>}
        </div>
        <div className="cards">
          <Card factory={factory} className="card warn" to="/arriving">
            <div className="k">{t("Overdue")}</div>
            <div className="v">{n(s.late.length + s.stalled.length)} <small>{t("POs or containers")}</small></div>
            <div className="line">
              {t("{late} late, {stalled} stalled.", { late: s.late.length, stalled: s.stalled.length })}{" "}
              {s.stalled[0]?.stalled[0]?.date
                ? t("Most overdue: {ref}, due {date}", { ref: refLabel(s.stalled[0].ref, s.stalled[0].kind), date: shortDate(s.stalled[0].stalled[0].date) })
                : s.late[0] ? t("Oldest line was due {date}", { date: shortDate(s.late[0].oldestLate) }) : ""}
            </div>
          </Card>
          <Card factory={factory} className="card" to="/arriving">
            <div className="k">{t("Arriving this week")}</div>
            <div className="v">{n(thisWeek.length)} <small>{t("deliveries")}</small></div>
            <div className="line">{thisWeek[0] ? t("Next: {ref} on {date}", { ref: refLabel(thisWeek[0].ref, thisWeek[0].kind), date: shortDate(thisWeek[0].next) }) : t("Nothing else due this week")}</div>
          </Card>
          <Card factory={factory} className="card" to="/arriving">
            <div className="k">{t("Stalled")}</div>
            <div className="v">{n(s.stalled.length)} <small>{t("POs")}</small></div>
            <div className="line">{t("Over 30 days late and never shipped. Chase or close in NetSuite")}</div>
          </Card>
        </div>
      </div>

      <div className="section">
        <div className="section-head">
          <h3>{t("Planning")}</h3>
        </div>
        <div className="linkrow">
          <Link to="/forecast">{t("Forecast")}</Link>
          {!factory && (
            <>
              <Link to="/trends">{t("Trends")}</Link>
              <Link to="/demand">{t("Yearly Demand")}</Link>
              <Link to="/accuracy">{t("Forecast accuracy")}</Link>
              <Link to="/overview">{t("Data status")}</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
